import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { SERVICES } from "@/data/services";
import { MaskHeading, EASE } from "@/components/site/motion";

const SPAN = {
  furniture: "sm:col-span-2 lg:col-span-6 lg:row-span-2",
  "tv-mounting": "lg:col-span-6",
  fitness: "lg:col-span-3",
  bbq: "lg:col-span-3",
  gazebos: "lg:col-span-4",
  sheds: "lg:col-span-4",
  blinds: "lg:col-span-4",
  shelving: "lg:col-span-4",
  gates: "lg:col-span-4",
};

const Tile = ({ s, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay: (i % 4) * 0.08 }}
    className={`${SPAN[s.id]} min-h-[260px]`}
  >
    <Link to={`/services#${s.id}`} data-testid={`bento-${s.id}`}
      className="group relative block h-full overflow-hidden bg-[var(--steel)] border border-[var(--line)] clip-cut-sm">
      <img src={s.img} alt={`${s.title} in Woodstock and Oxford County`} loading="lazy"
        className="absolute inset-0 w-full h-full object-cover grayscale-[35%] scale-105 transition-[transform,filter] duration-[1200ms] ease-[cubic-bezier(.16,1,.3,1)] group-hover:scale-110 group-hover:grayscale-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(15,16,18,0.95)] via-[rgba(15,16,18,0.45)] to-[rgba(15,16,18,0.1)]" />
      <div className="absolute top-4 left-4 font-mono-x text-[11px] tracking-[0.18em] text-white/85">{String(i + 1).padStart(2, "0")}</div>
      <div className="absolute top-4 right-4 w-10 h-10 grid place-items-center bg-[var(--orange)] text-[var(--ink)] translate-y-[-220%] group-hover:translate-y-0 transition-transform duration-500">
        <ArrowUpRight className="w-5 h-5" />
      </div>
      <div className="absolute bottom-0 inset-x-0 p-5 lg:p-6">
        <s.icon className="w-6 h-6 text-[var(--orange)] mb-3" />
        <h3 className={`font-display font-black uppercase leading-none ${s.id === "furniture" ? "text-4xl lg:text-6xl" : "text-2xl lg:text-3xl"}`}>{s.title}</h3>
        <p className="mt-2 text-sm text-zinc-300 max-w-md">{s.short}</p>
      </div>
    </Link>
  </motion.div>
);

export const ServiceBento = () => (
  <section className="relative bg-[var(--ink)] py-24 lg:py-36" data-testid="service-highlights">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-8 mb-14 lg:mb-20 items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow mb-5">What we build</p>
          <MaskHeading lines={[{ text: "If it comes" }, { text: "in a box,", className: "text-outline" }, { text: "we've got it." }]}
            className="font-display font-black uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.88]" />
        </div>
        <p className="lg:col-span-5 text-zinc-300 text-base lg:text-lg leading-relaxed">
          Skip the instruction manual and the missing-screw panic. Assemble Pro handles assembly and installation across Woodstock, Ingersoll and Oxford County — indoors and out.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[280px] gap-4 lg:gap-5">
        {SERVICES.map((s, i) => <Tile key={s.id} s={s} i={i} />)}
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.24 }} className="sm:col-span-2 lg:col-span-4 min-h-[260px]">
          <Link to="/contact" data-testid="bento-other-jobs"
            className="group relative h-full flex flex-col justify-between p-6 bg-[var(--orange)] text-[var(--ink)] clip-cut-sm overflow-hidden">
            <Plus className="w-12 h-12 transition-transform duration-700 group-hover:rotate-180" strokeWidth={2.5} />
            <div>
              <h3 className="font-display font-black uppercase text-3xl lg:text-4xl leading-none">Something else?</h3>
              <p className="mt-2 text-sm font-medium max-w-xs">Practically anything you buy that needs assembly or installing. Just ask.</p>
              <span className="mt-4 inline-flex items-center gap-2 font-display font-black uppercase tracking-wide">Ask about your job <ArrowUpRight className="w-4 h-4" /></span>
            </div>
          </Link>
        </motion.div>
      </div>
    </div>
  </section>
);
